import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import api from '../utils/apiClient';

const FavoritesContext = createContext();

export function FavoritesProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch favorites when user changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchFavorites();
    } else {
      setFavorites([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/favorites/${user.id}`);
      setFavorites(res.data.favorites || []);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
    setLoading(false);
  }, [user?.id]);

  const addFavorite = async (item, selectedSize, modifiers, specialNotes, extras = null, removedIngredients = null, nickname = null) => {
    if (!isAuthenticated || !user?.id) {
      toast.error('Please log in to save favorites');
      return false;
    }

    try {
      await api.post(`/favorites/${user.id}`, {
        item_id: item.id,
        item_name: item.name,
        item_image: item.image,
        selected_size: selectedSize,
        modifiers: modifiers,
        extras: extras,
        removed_ingredients: removedIngredients,
        special_instructions: specialNotes,
        nickname: nickname
      });
      toast.success('Added to favorites!');
      fetchFavorites();
      return true;
    } catch (err) {
      toast.error('Failed to save favorite');
      return false;
    }
  };

  const removeFavorite = async (favoriteId) => {
    if (!user?.id) return false;

    try {
      await api.delete(`/favorites/${user.id}/${favoriteId}`);
      toast.success('Removed from favorites');
      setFavorites(prev => prev.filter(f => f.id !== favoriteId));
      return true;
    } catch (err) {
      toast.error('Failed to remove favorite');
    }
    return false;
  };

  const updateFavorite = async (favoriteId, data) => {
    if (!user?.id) return false;

    try {
      await api.put(`/favorites/${user.id}/${favoriteId}`, data);
      toast.success('Favorite updated');
      fetchFavorites();
      return true;
    } catch (err) {
      toast.error('Failed to update favorite');
    }
    return false;
  };

  const isFavorite = (itemId, selectedSize = null) => {
    return favorites.some(f => 
      f.item_id === itemId && 
      (selectedSize === null || f.selected_size === selectedSize)
    );
  };

  return (
    <FavoritesContext.Provider value={{
      favorites,
      loading,
      addFavorite,
      removeFavorite,
      updateFavorite,
      isFavorite,
      fetchFavorites
    }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
